import { Injectable, computed, signal } from '@angular/core';

import { AccountStateResponse, UserRole } from '@app/core/models';

const isSupportedRole = (role: AccountStateResponse['role']): role is UserRole =>
  role === 'owner' || role === 'partner' || role === 'admin' || role === 'accountant';
const isCompletedStatus = (state: AccountStateResponse | null): boolean =>
  !!state && state.isLive !== false && (state.onboardingStatus === 'Live' || state.onboardingStatus === 'Completed');

@Injectable({ providedIn: 'root' })
export class AccountStateService {
  private readonly stateSignal = signal<AccountStateResponse | null>(null);
  private readonly loadingSignal = signal(false);

  readonly state = computed(() => this.stateSignal());
  readonly loading = computed(() => this.loadingSignal());
  readonly emailConfirmed = computed(() => this.stateSignal()?.emailConfirmed ?? true);
  readonly onboardingStatus = computed(() => this.stateSignal()?.onboardingStatus ?? 'Live');
  readonly isLive = computed(() => {
    const state = this.stateSignal();
    return isCompletedStatus(state) || state?.isLive === true || state?.onboardingCompleted === true;
  });
  readonly isLimitedAccess = computed(() => this.stateSignal()?.isLimitedAccess ?? false);
  readonly onboardingCompleted = computed(() => this.isLive());
  readonly ownerStatusWidget = computed(() => this.stateSignal()?.ownerStatusWidget ?? null);
  readonly blockedReasonCode = computed(() => this.stateSignal()?.blockedReasonCode ?? null);
  readonly allowedSections = computed(() => this.stateSignal()?.allowedSections ?? []);
  readonly currentOnboardingStep = computed(() => this.stateSignal()?.currentOnboardingStep ?? 'profile');
  readonly role = computed<UserRole | null>(() => {
    const role = this.stateSignal()?.role;
    return role && isSupportedRole(role) ? role : null;
  });

  setState(state: AccountStateResponse | null): void {
    this.stateSignal.set(state);
  }

  setLoading(loading: boolean): void {
    this.loadingSignal.set(loading);
  }

  clear(): void {
    this.stateSignal.set(null);
    this.loadingSignal.set(false);
  }

  getPostAuthRoute(homeRoute: string): string {
    const state = this.stateSignal();

    if (!state) {
      return homeRoute;
    }

    if (state.role !== 'owner') {
      return homeRoute;
    }

    if (state.onboardingStatus === 'RegistrationPending' || state.blockedReasonCode === 'RegistrationPendingPayment') {
      return '/register/pms';
    }

    if (state.blockedReasonCode === 'SubscriptionRequired') {
      return '/subscription/reactivate';
    }

    if (!state.emailConfirmed) {
      return '/dashboard';
    }

    if (!this.isLive()) {
      return '/dashboard';
    }

    return homeRoute;
  }
}
